import { CSSProperties, FC, useMemo } from 'react'
import {
  LAB_IMAGE_FORMATS,
  LAB_PRIVACY_CATEGORIES,
  LAB_PRIVACY_CATEGORY_KEYS,
  LAB_QUALITY_MAX,
  LAB_QUALITY_MIN,
  LAB_SKY_STYLES,
  LAB_TONECRAFT_PROFILES,
  LabEnhanceMode,
  LabEnhanceSettings,
  LabTonecraftProfile
} from './labSdk.types'
import { LabCheckbox, LabFeatureBlock, LabSectionTitle, LabSegmented, LabSlider, LabTile, LabTileGrid } from './LabPanelPrimitives.component'
import { toggleLabPrivacy, toggleLabPrivacyCategory } from './labSdk.mapping'
import type { OutputFormat, SkyStyle } from '@bkbnlab/api'
import { Stack, Typography } from '@mui/material'
import { BLUE_800, GRAY_700 } from 'constants/styling/theme'

import AspectRatioOutlinedIcon from '@mui/icons-material/AspectRatioOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TonalityOutlinedIcon from '@mui/icons-material/TonalityOutlined'
import TransformOutlinedIcon from '@mui/icons-material/TransformOutlined'
import WbCloudyOutlinedIcon from '@mui/icons-material/WbCloudyOutlined'
import skyClearThumb from 'assets/img/ai_settings/clear.webp'
import skyCloudyThumb from 'assets/img/ai_settings/cloudy.webp'
import skyLowCloudThumb from 'assets/img/ai_settings/low_cloud.webp'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

interface LabEnhanceOptionsProps {
  settings: LabEnhanceSettings
  onChange: (patch: Partial<LabEnhanceSettings>) => void
  disabled?: boolean
}

/** Lab has four sky styles; "neutral" has no thumbnail of its own, so the low-cloud one is desaturated. */
const SKY_THUMBS: Record<SkyStyle, { image: string, style?: CSSProperties }> = {
  clear: { image: skyClearThumb },
  low_cloud: { image: skyLowCloudThumb },
  neutral: { image: skyLowCloudThumb, style: { filter: 'saturate(.55) brightness(1.05)' } },
  high_cloud: { image: skyCloudyThumb },
}

/** Until `presets('tonecraft')` thumbnails are wired, the profile tiles preview the look on the current photo. */
const PROFILE_PREVIEW_FILTERS: Record<LabTonecraftProfile, CSSProperties | undefined> = {
  signature: undefined,
  natural: { filter: 'saturate(.8) contrast(.96)' },
  airy: { filter: 'brightness(1.12) saturate(.85) hue-rotate(-6deg)' },
  warm: { filter: 'sepia(.28) saturate(1.25) brightness(1.02)' },
}

/** Sky style tiles — shared by the Enhance options and the non-subscriber upsell */
export const useLabSkyTiles = (): Array<LabTile & { image: string }> => {
  const { t } = useTranslation(['photo_studio_editing'])
  return useMemo(
    () => LAB_SKY_STYLES.map((style) => ({ id: style, label: t(`lab.sky.${style}`), image: SKY_THUMBS[style].image, imageStyle: SKY_THUMBS[style].style })),
    [t]
  )
}

/**
 * The Enhance options of the Lab SDK engine — shown by the Enhance panel and by the Enhance section of "Display all
 * features at once". Basic (default) runs the recommended settings — Perspective, Tonecraft Natural + Auto levels, Auto
 * privacy — and only exposes Replace sky. Advanced lists every feature as a toggle row whose options expand underneath
 * when on; switching a feature off hides and disables its options without resetting them.
 */
export const LabEnhanceOptions: FC<LabEnhanceOptionsProps> = ({ settings, onChange, disabled = false }) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { previewImage, imageUrl } = useClientPhotoStudio()

  const skyTiles = useLabSkyTiles()

  const profileTiles: LabTile[] = useMemo(
    () => LAB_TONECRAFT_PROFILES.map((profile) => ({ id: profile, label: t(`lab.profile.${profile}`), image: previewImage || imageUrl, imageStyle: PROFILE_PREVIEW_FILTERS[profile] })),
    [t, previewImage, imageUrl]
  )

  const formatOptions = useMemo(() => LAB_IMAGE_FORMATS.map((format) => ({ value: format, label: t(`lab.format.${format}`) })), [t])

  const modeOptions = useMemo(
    () => [
      { value: LabEnhanceMode.BASIC, label: t('lab.mode.basic') },
      { value: LabEnhanceMode.ADVANCED, label: t('lab.mode.advanced') },
    ],
    [t]
  )

  const replaceSkyBlock = (
    <LabFeatureBlock icon={<WbCloudyOutlinedIcon />} label={t('lab.feature.replace_sky')} on={settings.replaceSky} onToggle={(on) => onChange({ replaceSky: on })} disabled={disabled}>
      <LabTileGrid tiles={skyTiles} selected={settings.skyStyle} onSelect={(id) => onChange({ skyStyle: id as SkyStyle })} disabled={disabled} />
    </LabFeatureBlock>
  )

  return (
    <>

      <LabSegmented large options={modeOptions} value={settings.mode} onChange={(mode) => onChange({ mode })} disabled={disabled} />

      {settings.mode === LabEnhanceMode.BASIC && (
        <Stack gap="0.6rem" sx={{ padding: '2.4rem 0.4rem 1.2rem' }}>

          <LabSectionTitle>{t('lab.included_title')}</LabSectionTitle>

          {[
            { icon: <TransformOutlinedIcon />, label: t('lab.included.perspective') },
            { icon: <TonalityOutlinedIcon />, label: t('lab.included.tonecraft_signature') },
            { icon: <ShieldOutlinedIcon />, label: t('lab.included.privacy') },
          ].map((item) => (
            <Stack key={item.label} flexDirection="row" alignItems="center" gap="0.8rem" sx={{ height: '2.4rem', color: GRAY_700, '& .MuiSvgIcon-root': { fontSize: '1.6rem' } }}>

              {item.icon}

              <Typography sx={{ flex: 1, fontSize: '1.4rem', fontWeight: 350, color: GRAY_700, letterSpacing: '-0.56px', lineHeight: 1.4 }}>{item.label}</Typography>

              <CheckCircleOutlineIcon sx={{ color: BLUE_800 }} />

            </Stack>
          ))}

        </Stack>
      )}

      {settings.mode === LabEnhanceMode.BASIC && replaceSkyBlock}

      {settings.mode === LabEnhanceMode.ADVANCED && (
        <Stack sx={{ marginTop: '0.4rem' }}>

          <LabFeatureBlock first icon={<TransformOutlinedIcon />} label={t('lab.feature.perspective')} on={settings.perspective} onToggle={(on) => onChange({ perspective: on })} disabled={disabled}>
            <LabSlider
              label={t('lab.strength')}
              value={settings.perspectiveStrength}
              valueLabel={settings.perspectiveStrength.toFixed(1)}
              min={0.1}
              max={1}
              step={0.1}
              onChange={(value) => onChange({ perspectiveStrength: value })}
              disabled={disabled}
            />
          </LabFeatureBlock>

          <LabFeatureBlock icon={<TonalityOutlinedIcon />} label={t('lab.feature.tonecraft')} on={settings.tonecraft} onToggle={(on) => onChange({ tonecraft: on })} disabled={disabled}>

            <LabCheckbox label={t('lab.auto_levels')} info={t('lab.auto_levels_info')} checked={settings.autoLevels} onChange={(checked) => onChange({ autoLevels: checked })} disabled={disabled} />

            <LabTileGrid tiles={profileTiles} selected={settings.tonecraftProfile} onSelect={(id) => onChange({ tonecraftProfile: id as LabTonecraftProfile })} disabled={disabled} labelFontSize="1.2rem" />

          </LabFeatureBlock>

          <LabFeatureBlock icon={<ShieldOutlinedIcon />} label={t('lab.feature.privacy')} on={settings.privacy} onToggle={(on) => onChange(toggleLabPrivacy(settings, on))} disabled={disabled}>
            <Stack gap="0.4rem">
              {LAB_PRIVACY_CATEGORIES.map((category) => (
                <LabCheckbox
                  key={category}
                  label={t(`lab.privacy.${LAB_PRIVACY_CATEGORY_KEYS[category]}`)}
                  checked={settings.privacyCategories.includes(category)}
                  onChange={(checked) => onChange(toggleLabPrivacyCategory(settings, category, checked))}
                  disabled={disabled}
                />
              ))}
            </Stack>
          </LabFeatureBlock>

          {replaceSkyBlock}

          <LabFeatureBlock icon={<AspectRatioOutlinedIcon />} label={t('lab.feature.image_format')} on={settings.imageFormat} onToggle={(on) => onChange({ imageFormat: on })} disabled={disabled}>

            <LabSegmented options={formatOptions} value={settings.format} onChange={(format: OutputFormat) => onChange({ format })} disabled={disabled} />

            {settings.format !== 'png' && (
              <LabSlider
                label={t('lab.quality')}
                value={settings.quality}
                valueLabel={String(settings.quality)}
                min={LAB_QUALITY_MIN}
                max={LAB_QUALITY_MAX}
                step={1}
                onChange={(value) => onChange({ quality: value })}
                disabled={disabled}
              />
            )}

          </LabFeatureBlock>

        </Stack>
      )}

    </>
  )
}
